import { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  FormControl,
  Grid,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  Alert,
} from '@mui/material';
import { Handshake as HandshakeIcon, Business as BusinessIcon } from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'react-toastify';

import SponsorPublicShell from '@components/sponsor/SponsorPublicShell';
import { useAuthStore } from '@store/authStore';
import authService from '@services/authService';
import { CORE_SPORTS } from '@utils/sports';
import {
  SPONSOR_BUDGET_BANDS,
  SPONSOR_GEOGRAPHY_OPTIONS,
  SPONSOR_INTEREST_CATEGORIES,
  SPONSORSHIP_TIERS,
} from '@utils/sponsor';

const schema = z.object({
  email: z.string().email('Invalid email address'),
  password: z
    .string()
    .min(8, 'Password must be at least 8 characters')
    .regex(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/, 'Must include uppercase, lowercase, and number'),
  companyName: z.string().min(2, 'Company name is required'),
  contactName: z.string().min(2, 'Contact name is required'),
  phone: z.string().min(8, 'Phone number is required'),
  websiteUrl: z.string().optional(),
  country: z.string().min(1),
  industry: z.string().optional(),
  preferredTier: z.string(),
  budgetBand: z.string(),
  sports: z.array(z.string()).min(1, 'Select at least one sport'),
  interestCategories: z.array(z.string()).min(1, 'Select at least one interest'),
});

type FormData = z.infer<typeof schema>;

const RegisterSponsorPage = () => {
  const navigate = useNavigate();
  const { login } = useAuthStore();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      country: 'India',
      preferredTier: 'flexible',
      budgetBand: 'flexible',
      sports: ['Badminton'],
      interestCategories: ['contest'],
    },
  });

  const onSubmit = async (data: FormData) => {
    setLoading(true);
    setError('');
    try {
      const response = await authService.registerSponsor({
        email: data.email,
        password: data.password,
        companyName: data.companyName,
        contactName: data.contactName,
        phone: data.phone,
        websiteUrl: data.websiteUrl,
        country: data.country,
        industry: data.industry,
        preferredTier: data.preferredTier,
        budgetBand: data.budgetBand,
        sports: data.sports,
        interestCategories: data.interestCategories,
      });
      if (response?.user) {
        login(response.user);
        toast.success(`Welcome, ${data.companyName}! Your sponsor account is ready.`);
        navigate('/sponsor/dashboard');
      } else {
        throw new Error('Invalid registration response');
      }
    } catch (err: any) {
      const msg = err.response?.data?.message || err.message || 'Registration failed';
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const hero = (
    <Box sx={{ py: { xs: 4, md: 6 }, textAlign: 'center' }}>
      <Chip
        icon={<HandshakeIcon />}
        label="Sponsor partner registration"
        sx={{ mb: 2, bgcolor: 'rgba(245,158,11,0.15)', color: '#fcd34d', fontWeight: 800, border: '1px solid rgba(245,158,11,0.35)' }}
      />
      <Typography variant="h3" sx={{ fontWeight: 900, color: '#f8fafc', mb: 1 }}>
        Create your sponsor account
      </Typography>
      <Typography sx={{ color: '#94a3b8', maxWidth: 560, mx: 'auto' }}>
        Multi-tier partnerships matched to your budget and competition goals. Global platform, India-first rollout.
        No public pricing — packages are tailored in conversation.
      </Typography>
    </Box>
  );

  return (
    <SponsorPublicShell hero={hero}>
      <Box
        sx={{
          maxWidth: 820,
          mx: 'auto',
          p: { xs: 2.5, md: 4 },
          borderRadius: 3,
          background: 'linear-gradient(145deg, rgba(30,41,59,0.95) 0%, rgba(15,23,42,0.98) 100%)',
          border: '1px solid rgba(255,255,255,0.1)',
          boxShadow: '0 24px 64px rgba(0,0,0,0.45)',
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 3 }}>
          <BusinessIcon sx={{ color: '#f59e0b', fontSize: 32 }} />
          <Typography variant="h5" sx={{ fontWeight: 800, color: '#f1f5f9' }}>
            Company & partnership profile
          </Typography>
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={2.5}>
            <Grid item xs={12}>
              <TextField
                {...register('companyName')}
                label="Company / Brand name"
                fullWidth
                error={!!errors.companyName}
                helperText={errors.companyName?.message}
                sx={fieldSx}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                {...register('contactName')}
                label="Primary contact name"
                fullWidth
                error={!!errors.contactName}
                helperText={errors.contactName?.message}
                sx={fieldSx}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                {...register('phone')}
                label="Phone"
                fullWidth
                error={!!errors.phone}
                helperText={errors.phone?.message}
                sx={fieldSx}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                {...register('email')}
                label="Work email"
                type="email"
                fullWidth
                error={!!errors.email}
                helperText={errors.email?.message}
                sx={fieldSx}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                {...register('password')}
                label="Password"
                type="password"
                fullWidth
                error={!!errors.password}
                helperText={errors.password?.message}
                sx={fieldSx}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField {...register('websiteUrl')} label="Website (optional)" fullWidth sx={fieldSx} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField {...register('industry')} label="Industry (optional)" fullWidth sx={fieldSx} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="country"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth sx={fieldSx}>
                    <InputLabel>Country / region focus</InputLabel>
                    <Select {...field} label="Country / region focus">
                      {SPONSOR_GEOGRAPHY_OPTIONS.map((g) => (
                        <MenuItem key={g} value={g}>
                          {g}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="preferredTier"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth sx={fieldSx}>
                    <InputLabel>Preferred tier</InputLabel>
                    <Select {...field} label="Preferred tier">
                      {SPONSORSHIP_TIERS.map((t) => (
                        <MenuItem key={t.id} value={t.id}>
                          {t.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="budgetBand"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth sx={fieldSx}>
                    <InputLabel>Budget band</InputLabel>
                    <Select {...field} label="Budget band">
                      {SPONSOR_BUDGET_BANDS.map((b) => (
                        <MenuItem key={b.id} value={b.id}>
                          {b.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="sports"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth sx={fieldSx} error={!!errors.sports}>
                    <InputLabel>Sports of interest</InputLabel>
                    <Select {...field} multiple label="Sports of interest" renderValue={(v) => (v as string[]).join(', ')}>
                      {CORE_SPORTS.map((s) => (
                        <MenuItem key={s} value={s}>
                          {s}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="interestCategories"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth sx={fieldSx} error={!!errors.interestCategories}>
                    <InputLabel>Partnership interests</InputLabel>
                    <Select
                      {...field}
                      multiple
                      label="Partnership interests"
                      renderValue={(v) =>
                        (v as string[])
                          .map((id) => SPONSOR_INTEREST_CATEGORIES.find((c) => c.id === id)?.label || id)
                          .join(', ')
                      }
                    >
                      {SPONSOR_INTEREST_CATEGORIES.map((c) => (
                        <MenuItem key={c.id} value={c.id}>
                          {c.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>
          </Grid>

          <Button
            type="submit"
            fullWidth
            size="large"
            disabled={loading}
            sx={{
              mt: 3,
              py: 1.5,
              fontWeight: 800,
              textTransform: 'none',
              fontSize: '1.05rem',
              background: 'linear-gradient(135deg, #f59e0b 0%, #ea580c 100%)',
              boxShadow: '0 12px 32px rgba(245, 158, 11, 0.35)',
              '&:hover': { background: 'linear-gradient(135deg, #fbbf24 0%, #f97316 100%)' },
            }}
          >
            {loading ? 'Creating account…' : 'Register as sponsor partner'}
          </Button>

          <Typography variant="body2" sx={{ mt: 2, textAlign: 'center', color: '#94a3b8' }}>
            Already registered?{' '}
            <Link component={RouterLink} to="/login" sx={{ color: '#fbbf24', fontWeight: 700 }}>
              Sign in
            </Link>
            {' · '}
            <Link component={RouterLink} to="/sponsors" sx={{ color: '#a5b4fc' }}>
              Browse partnerships
            </Link>
          </Typography>
        </Box>
      </Box>
    </SponsorPublicShell>
  );
};

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    color: '#e2e8f0',
    '& fieldset': { borderColor: 'rgba(255,255,255,0.15)' },
    '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
  },
  '& .MuiInputLabel-root': { color: '#94a3b8' },
  '& .MuiFormHelperText-root': { color: '#f87171' },
};

export default RegisterSponsorPage;
